const NUMBER_TOKEN = /^\d+[A-Za-z]?\.?$/;
const SLUG_START = /^(?:INT|EXT|EST|I\/E|E\/I)(?:[./\s]|$)/i;
const PAGE_BREAK_LINE = /^\s*-{2,}\s*PAGE\s*BREAK\s*-{2,}\s*$/i;

export interface HeadingSource {
  header?: string | null;
  scene_number?: number | string | null;
  int_ext?: string | null;
  location_name?: string | null;
  time_of_day?: string | null;
}

function sameNumber(token: string, sceneNumber: string): boolean {
  return sceneNumber !== '' && token.replace(/\.$/, '').toUpperCase() === sceneNumber;
}

export function cleanSceneHeader(header: string | null | undefined, sceneNumber?: number | string | null): string {
  const tokens = String(header ?? '')
    .replace(/#[^#\s]+#/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  const number = sceneNumber == null ? '' : String(sceneNumber).trim().toUpperCase();
  const firstWord = tokens.find((t) => !NUMBER_TOKEN.test(t)) ?? '';
  const slugFollows = SLUG_START.test(firstWord);

  let start = 0;
  while (start < tokens.length - 1 && NUMBER_TOKEN.test(tokens[start]) && (slugFollows || sameNumber(tokens[start], number))) {
    start++;
  }
  let end = tokens.length;
  if (start > 0) {
    while (end - 1 > start && NUMBER_TOKEN.test(tokens[end - 1]) && sameNumber(tokens[end - 1], number)) end--;
  }
  return tokens.slice(start, end).join(' ');
}

export function sceneHeading(scene: HeadingSource): string {
  const cleaned = cleanSceneHeader(scene.header, scene.scene_number);
  if (cleaned) return cleaned;
  const parts = [scene.int_ext ? `${scene.int_ext}.` : '', scene.location_name ?? ''].filter(Boolean).join(' ');
  return [parts, scene.time_of_day ?? ''].filter(Boolean).join(' - ');
}

function headingKey(text: string, sceneNumber?: number | string | null): string {
  return cleanSceneHeader(text, sceneNumber).toUpperCase().replace(/\s*-\s*/g, '-');
}

export function cleanSceneBody(rawText: string | null | undefined, scene: HeadingSource = {}): string {
  const lines = String(rawText ?? '').split(/\r?\n/).filter((line) => !PAGE_BREAK_LINE.test(line));
  const first = lines.findIndex((line) => line.trim() !== '');
  if (first !== -1) {
    const heading = headingKey(sceneHeading(scene), scene.scene_number);
    const candidate = headingKey(lines[first], scene.scene_number);
    if (candidate && candidate === heading) lines.splice(0, first + 1);
  }
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function isFountainSlug(heading: string): boolean {
  return /^(?:int|ext|est|i\/e)[. ]/i.test(heading);
}
